import { Component } from '@theme/component';

/**
 * @typedef {object} TabsRefs
 * @property {HTMLElement[]} tabs - Tab button elements ([role="tab"])
 * @property {HTMLElement[]} panels - Tab panel elements ([role="tabpanel"])
 */

/**
 * A custom element that manages ARIA tab switching with keyboard navigation.
 *
 * @extends {Component<TabsRefs>}
 */
class TabsComponent extends Component {
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    // Read initial active tab from server-rendered aria-selected
    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#updateActiveTab();
    this.#setupListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #setupListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    for (const [i, tab] of (this.refs.tabs ?? []).entries()) {
      tab.addEventListener('click', () => this.#activate(i), opts);
    }

    this.addEventListener('keydown', this.#handleKeydown, opts);
  }

  /** @param {KeyboardEvent} e */
  #handleKeydown = (e) => {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const tabs = this.refs.tabs ?? [];
    const i = tabs.indexOf(target);
    if (i === -1) return;

    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -i,
      End: tabs.length - 1 - i,
    };

    const offset = navMap[e.key];
    if (offset !== undefined) {
      e.preventDefault();
      const next = (i + offset + tabs.length) % tabs.length;
      tabs[next]?.focus();
      this.#activate(next);
    }
  };

  /** @param {number} index */
  #activate(index) {
    const { tabs } = this.refs;
    if (!tabs || index === this.#active || index < 0 || index >= tabs.length) return;
    this.#active = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of (tabs ?? []).entries()) {
      const active = i === this.#active;
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
    }

    for (const [i, panel] of (panels ?? []).entries()) {
      panel.toggleAttribute('inert', i !== this.#active);
    }
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
